import React, { useState, useEffect, useMemo, useRef } from 'react'
import { read } from 'xlsx'
import { toast } from '@/modules/base/components/toast/toastConfig'
import { makeStyles } from '@mui/styles'
import PropTypes from 'prop-types'
import { TextInput, TextInputWithIcon } from '@/modules/base/components/inputs/index'
import debounce from 'lodash/debounce'
import { get, groupBy, isNumber } from 'lodash'

import lang from '@/lang'
import langSub from '@/utils/langSub'

import Loader from '@/modules/base/components/loader/loader'
import Topbar from '@/modules/base/components/topbar/topbar'
import FileInput from 'react-fine-uploader/file-input'
import { getUploader } from '@/modules/file/uploader'
import ButtonIconSquare from '@/modules/base/components/buttons/icon-square/button-icon-square'
import { RadioInput } from '@/modules/base/components/inputs'
import DOAccordion from '@/modules/base/do-accordion'
import PerfectScrollbar from 'react-perfect-scrollbar'

import { IMPORT_TYPE_ARTICLES, IMPORT_TYPE_CUSTOMERS, IMPORT_TYPE_SUPPLIERS } from '@/modules/import/mass-import/mass-import-service'
import { MassImportService } from './mass-import-service'
import MassImportAssignment from './mass-import-assignment/mass-import-assignment'
import MassImportPreview from './mass-import-preview/mass-import-preview'
import { modalConfirmation, useOpenModal } from '@/modules/_helpers/openModal'
import { useWebSocketEvent } from '@/modules/webSocket'
import { EventTypes } from '@/modules/webSocket/constants'
import SVGIcon from '@/modules/base/components/svg-icon/svg-icon'
import IconUploadFile from '@assets/images/icons/upload-file.svg'
import IconFolder from '@assets/images/icons/folder.svg'
import IconImportWarning from '@assets/images/icons/importWarning.svg'
import IconReset from '@assets/images/icons/reset.svg'
import { CancelButton, SquareIconButton } from '@/modules/base/components/buttons'

import Label from '@/modules/base/components/texts/label'
import { isMeinBuero } from '@/utils/build-env'

const useStyles = makeStyles(theme => ({
  moreOptions: {
    '& > :not(:last-child)': {
      marginBottom: 10,
    },
    '& > *': {
      borderRadius: 2,
    },
  },
  errorList: {
    marginLeft: 20,
    listStyleType: 'disc',
  },
  confirmIcon: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    margin: '30px 0 50px',
    fontSize: 30,
    fontWeight: 600,
    color: '#414a5f',
    '& .SVGInline': {
      position: 'absolute',
    },
  },
  loader: {
    height: '100%',
    top: 85,
  },
  importLine: {
    display: 'flex',
  },
  importLineLabel: {
    flexGrow: 1,
    textAlign: 'left',
    display: 'flex',
    alignItems: 'center',
    color: '#424a5f',
    fontWeight: 500,
  },
  importLineInput: {
    width: 78,
  },
  label: {
    textAlign: 'left',
    margin: '10px 0',
    fontWeight: 600,
    fontSize: 16,
  },
  radioOptionsContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
  },
  radio: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  customOptionClass: {
    paddingBottom: 15,
    '& > span': {
      textAlign: 'left',
    },
  },
  optionBool: {
    display: 'flex',
    flexDirection: 'column',
    marginBottom: 10,
  },
  boolInputWrapper: {
    display: 'flex',
    '& > div': {
      '& > div': {
        width: '100%',
      },
    },
    '& > :nth-child(1)': {
      marginRight: 10,
    },
  },
  boolCaptionLabel: {
    flexGrow: 1,
    padding: '15px 0',
    color: '#424a5f',
    fontWeight: 500,
  },
  boolInput: {
    textAlign: 'left',
    '& label': {
      fontSize: 11,
    },
  },
  container: {
    height: '100vh',
    width: '100%',
    background: '$colorWhite',
    display: 'flex',
    flexDirection: 'column',
  },
  dataImportContinueButton: {
    marginLeft: '15px',
  },
  dataImportCancelButton: {
    marginLeft: '15px',
  },
  faqLinkContainer: {
    margin: '10px 0',
  },
  faqLink: {
    color: theme.colorPrimaryButtonBackground,
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  master: {
    minWidth: 378,
    maxWidth: 378,
    borderRight: theme.border,
    height: '100%',
    padding: '32px 31.1px 350.6px 32px',
    backgroundColor: '#fff',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    '& > *': {
      width: '100%',
    },
  },
  detail: {
    display: 'flex',
    backgroundColor: 'transparent',
    flexDirection: 'column',
    alignItems: 'center',
    '& > :first-child': {
      marginBottom: 32,
    },
    '& > *': {
      maxWidth: 870,
      width: '100%',
    },
  },
  scrollbarContainer: {
    boxShadow: '0px 16px 20px -20px inset lightgray',
    flexGrow: 1,
    padding: 40,
  },
  masterDetail: {
    display: 'flex',
    maxHeight: `calc(100vh - ${theme.pageContentOffset}px)`,
    flexGrow: 1,
    backgroundColor: theme.backgroundColor,
  },
}))

const { massImport: texts } = lang

const allowedExtensions = ['xlsx', 'xls']
const acceptAttribute = ['.xlsx', '.xls']

const CUSTOMER_IMPORT_FAQ_URL = isMeinBuero() ? 'https://www.buhl.de/go/46461' : 'https://info.orgamax.de/faq-orgamax-buchhaltung/der-kunden-import'

const IMPORT_OPTIONS = {
  create: 'create',
  createUpdate: 'createUpdate',
  update: 'update',
}

const getRowIndex = (cells) => {
  const cellsWithCount = cells.reduce((result, cell) => {
    const cellNumber = cell.replace(/[^0-9]/gi, '')
    if (cellNumber && isNumber(Number(cellNumber))) {
      if (result[cellNumber]) {
        result[cellNumber] = result[cellNumber] + 1
      } else {
        result[cellNumber] = 1
      }
    }
    return result
  }, {})

  let lockCellnumber = false
  let rowCount = 0
  return Object.keys(cellsWithCount).reduce((result, cellNumberKey) => {
    if (cellsWithCount[cellNumberKey] > rowCount && !lockCellnumber) {
      result = cellNumberKey
      rowCount = cellsWithCount[cellNumberKey]
    } else {
      lockCellnumber = true
    }
    return result
  }, '1')
}

const MassImportComponent = (props) => {
  const {
    importType = IMPORT_TYPE_ARTICLES,
    data,
    fileName: givenFileName,
    abort,
  } = props

  const classes = useStyles()

  const transformedData = data ? transformWorkBook(data) : { rows: [], columns: {} }

  const getSubKey = () => {
    switch (importType) {
      case IMPORT_TYPE_ARTICLES:
        return 'massImport.articles'
      case IMPORT_TYPE_CUSTOMERS:
        return 'massImport.customers'
      case IMPORT_TYPE_SUPPLIERS:
        return 'massImport.suppliers'
    }
  }

  const importOptions = [
    { value: IMPORT_OPTIONS.create, label: <span className="radio-label">{langSub('massImport2.importKind.create', [{ template: '{{items}}', subKey: getSubKey() }])}</span> },
    { value: IMPORT_OPTIONS.createUpdate, label: <span className="radio-label">{langSub('massImport2.importKind.createUpdate', [{ template: '{{items}}', subKey: getSubKey() }])}</span> },
    { value: IMPORT_OPTIONS.update, label: <span className="radio-label">{langSub('massImport2.importKind.update', [{ template: '{{items}}', subKey: getSubKey() }])}</span> },
  ]

  const [importLine, setImportLine] = useState(1)
  const [importData, setImportData] = useState(transformedData)
  const [rawData, setRawData] = useState(transformedData)
  const [importSchema, setImportSchema] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [isImporting, setIsImporting] = useState(false)
  const [assignmentData, setAssignmentData] = useState({})
  const [importOption, setImportOption] = useState(importOptions[0])
  const [fileName, setFilename] = useState(givenFileName)
  const [boolTrue, setBoolTrue] = useState('ja')
  const [boolFalse, setBoolFalse] = useState('nein')
  const [currentJobId, setCurrentJobId] = useState(null)

  useEffect(() => {
    async function fetch() {
      try {
        let schema
        switch (importType) {
          case IMPORT_TYPE_ARTICLES:
            schema = await MassImportService.getImportSchema('article')
            break
          case IMPORT_TYPE_CUSTOMERS:
            schema = await MassImportService.getImportSchema('customer')
            break
          case IMPORT_TYPE_SUPPLIERS:
            schema = await MassImportService.getImportSchema('supplier')
            break
        }
        setImportSchema(schema)
      } catch (e) {
        // swallow
      }
    }
    fetch()
  }, [])

  useEffect(() => {
    const rows = rawData.rows.reduce((result, current) => current ? [...result, current] : result, []).slice((Number(importLine) || 1) - 1)
    setImportData({ ...rawData, rows })
  }, [importLine, rawData])

  const onChangeBool = useMemo(() => {
    return debounce((value, type) => {
      if (type) {
        setBoolTrue(value)
      } else {
        setBoolFalse(value)
      }
    }, 300)
  }, [])

  const onChangeAssignmentData = (data) => {
    const newData = { ...assignmentData, ...data }
    setAssignmentData(Object.keys(newData).reduce((result, current) => newData[current] ? { ...result, [current]: newData[current] } : result, {}))
  }

  const onUploaderError = (id, name, errorReason, xhr) => {
    toast.showToast({ type: 'error', message: errorReason })

    setIsLoading(false)
  }

  const onFileReady = (id, name) => {
    const file = uploader.methods.getFile(id)
    const reader = new FileReader()
    reader.onabort = (error) => {
      toast.showToast({ type: 'error', message: error })
    }
    reader.onerror = (error) => {
      toast.showToast({ type: 'error', message: error })
    }
    reader.onload = () => {
      const data = transformWorkBook(reader.result)
      setFilename(file.name)

      setRawData(data)
      setIsLoading(false)
    }
    reader.readAsBinaryString(file)

    setIsLoading(true)
  }

  const uploader = getUploader({
    autoUpload: false,
    messages: {
      typeError: lang.fileUploaderTypeError.replace('%s', allowedExtensions.join(',')),
    },
    request: {
    },
    callbacks: {
      onError: onUploaderError,
      onSubmitted: onFileReady,
    },
  })

  const convertBoolean = (value) => {
    switch (`${value}`.toLowerCase()) {
      case boolTrue:
        return true
      case boolFalse:
        return false
      default:
        return null
    }
  }

  const prepareData = (rows, assignmentData) => {
    return rows.reduce((result, row) => {
      const preparedRow = Object.keys(row).reduce((rowResult, rowKey) => {
        const assignment = assignmentData[rowKey]
        if (assignment) {
          const value = assignment.type === 'boolean'
            ? convertBoolean(row[rowKey].value)
            : assignment.type === 'string'
              ? `${row[rowKey].value}`
              : row[rowKey].value
          rowResult[assignment.key] = {
            value,
            excelId: row[rowKey].excelId,
            metaData: assignment.metaData,
          }
        }
        return rowResult
      }, {})

      // Empty Excel cells are omitted from `row`; still send '' for mapped custom fields so updates can clear them.
      const rowExcelNumber = Object.values(row).find(cell => cell?.excelId)?.excelId?.replace(/^[A-Z]+/i, '') || ''
      Object.keys(assignmentData).forEach(columnKey => {
        const assignment = assignmentData[columnKey]
        if (assignment?.metaData?.table === 'customValues' && !row[columnKey] && !preparedRow[assignment.key]) {
          preparedRow[assignment.key] = {
            value: '',
            excelId: `${columnKey}${rowExcelNumber}`,
            metaData: assignment.metaData,
          }
        }
      })

      result.push(preparedRow)
      return result
    }, [])
  }

  useWebSocketEvent(EventTypes.MASS_IMPORT_JOB_STATUS, ({ data }) => {
    if (currentJobId && data.jobId === currentJobId) {
      const { state } = data
      setIsImporting(false)
      setCurrentJobId(null)

      if (state === 'completed') {
        toast.showToast({ message: texts.massSuccess })
      } else if (state === 'failed') {
        toast.showToast({ message: texts.massError, type: 'error' })
      }
    }
  })

  const send = () => {
    setIsImporting(true)
    const data = prepareData(importData.rows, assignmentData)
    toast.showToast({ message: texts.importStarted })

    let importRequest
    switch (importType) {
      case IMPORT_TYPE_ARTICLES:
        importRequest = MassImportService.postArticleMassImport({ data, importKind: importOption.value })
        break
      case IMPORT_TYPE_CUSTOMERS:
        importRequest = MassImportService.postCustomerMassImport({ data, importKind: importOption.value })
        break
      case IMPORT_TYPE_SUPPLIERS:
        importRequest = MassImportService.postSupplierMassImport({ data, importKind: importOption.value })
        break

      default:
        toast.showToast({ message: texts.massError, type: 'error' })
        setIsImporting(false)
        break
    }

    importRequest?.then((response) => {
      const jobId = response?.jobId
      setCurrentJobId(jobId)
    }).catch(() => {
      toast.showToast({ message: texts.massError, type: 'error' })
      setIsImporting(false)
    })
  }

  const onContinue = () => {
    const getSubKey = () => {
      switch (importType) {
        case IMPORT_TYPE_ARTICLES:
          return 'massImport.itemsArticles'
        case IMPORT_TYPE_CUSTOMERS:
          return 'massImport.itemsCustomers'
        case IMPORT_TYPE_SUPPLIERS:
          return 'massImport.itemsSuppliers'
      }
    }

    const html = langSub('massImport.confirmDescription', [{ template: '{{items}}', subKey: getSubKey() }])

    modalConfirmation(send, {
      title: texts.confirmTitle,
      description: (<div dangerouslySetInnerHTML={{ __html: html }}></div>),
      confirmButtonLabel: texts.confirmButton,
      abortButtonLabel: texts.cancelButton,
      icon: (<span className={classes.confirmIcon}><SVGIcon svg={IconImportWarning} width='64px' height='64px' /></span>),
    })
  }

  const onChangeImportKind = (value) => {
    const newImportOption = importOptions.find(option => option.value === value)
    setImportOption(newImportOption)
  }

  const onSetImportLine = useMemo(() => {
    return debounce((value) => {
      setImportLine(value)
    }, 300)
  }, [])

  const commonProps = {
    classes,
    importSchema,
    importData,
    assignmentData,
    importType,
    importLine,
    importOption,
  }

  const boolProps = {
    classes,
    boolTrue,
    boolFalse,
    onChangeBool,
  }

  const importLineProps = {
    classes,
    importLine,
    onChange: onSetImportLine,
  }

  const importOptionProps = {
    options: importOptions,
    value: importOption.value,
    onChange: onChangeImportKind,
  }

  return (
    <div className={classes.container}>
      <Loader text={texts.validation} visible={isLoading} customClass={classes.loader} />
      <MassImportTopBar {...{ ...commonProps, onContinue, abort, boolTrue, boolFalse, onReset: () => setAssignmentData({}), isImporting }} />
      <div className={classes.masterDetail}>
        <Master {...{ ...commonProps, fileName, uploader, boolProps, importLineProps, importOptionProps }} />
        <Detail {...{ ...commonProps, onChangeAssignmentData }} />
      </div>
    </div>
  )
}

MassImportComponent.propTypes = {
  abort: PropTypes.func.isRequired,
}

export default MassImportComponent

const OptionImportLine = ({ classes, importLine, onChange }) => (
  <span className={classes.importLine}>
    <Label className={classes.importLineLabel} size="large">{texts.importLine}</Label>
    <TextInput className={classes.importLineInput} useFormik={false} name='importLine' value={importLine} onChange={onChange} />
  </span>
)

const OptionBool = ({ classes, boolTrue, boolFalse, onChangeBool }) => (
  <span className={classes.optionBool}>
    <Label className={classes.boolCaptionLabel} size="large">{texts.boolean}</Label>
    <span className={classes.boolInputWrapper}>
      <TextInput useFormik={false}
        className={classes.boolInput}
        name='boolTrue'
        label={lang.massImport.boolLabelTrue}
        value={boolTrue}
        onChange={(e) => onChangeBool(e, true)}
        required />
      <TextInput useFormik={false}
        className={classes.boolInput}
        name='boolFalse'
        label={lang.massImport.boolLabelFalse}
        value={boolFalse}
        onChange={(e) => onChangeBool(e, false)}
        required />
    </span>
  </span>
)

const MassImportTopBar = ({ classes, importType, abort, onContinue, importOption, importSchema, assignmentData, boolTrue, boolFalse, onReset, isImporting }) => {
  const [errors, setErrors] = useState([])
  const openModal = useOpenModal()

  const showErrors = () => {
    const description = <ul className={classes.errorList}>{errors.map((error) => <li key={error}>{get(texts, `errors.massImport.${error}`)}</li>)}</ul>
    openModal((close) => ({
      component: 'ConfirmModal',
      props: {
        title: texts.errors.title,
        description,
        confirmButtonLabel: lang.generics.understood,
        noCancel: true,
        onConfirm: () => {
          close()
        },
        onAbort: () => {
          close()
        },
      },
    }))
  }

  useEffect(() => {
    const requiredFields = importSchema.filter(({ required }) => !!required)
    // Fields with a `requiredGroup` (e.g. customer import: last name OR company name) are validated
    // together below - at least one member of the group must be assigned, not every single one.
    const requiredKeys = requiredFields.filter(({ requiredGroup }) => !requiredGroup).map(({ key }) => key)
    const requiredGroups = groupBy(requiredFields.filter(({ requiredGroup }) => requiredGroup), 'requiredGroup')
    const availableKeys = Object.keys(assignmentData).map((key) => assignmentData[key].key)

    const errors = requiredKeys.filter((key) => !availableKeys.includes(key)).map((key) => `${importType}.${key}`)

    // The error message key follows the `<importType>.<requiredGroup>Missing` convention, e.g.
    // `customers.billingAddressNameMissing` for schema fields with `requiredGroup: 'billingAddressName'`.
    Object.keys(requiredGroups).forEach((groupKey) => {
      const groupKeys = requiredGroups[groupKey].map(({ key }) => key)
      if (!groupKeys.some((key) => availableKeys.includes(key))) {
        errors.push(`${importType}.${groupKey}Missing`)
      }
    })

    if (!boolTrue || !boolFalse) {
      errors.push('booelanValueMissing')
    }
    if (boolTrue === boolFalse) {
      errors.push('booelanValueEqual')
    }

    setErrors(errors)
  }, [importOption, importSchema, assignmentData, boolTrue, boolFalse, importType])

  return (
    <Topbar variant='white' title={texts.title.replace('{{type}}', texts[importType])}>
      <SquareIconButton icon={IconReset} onClick={onReset} id='resetButton' variant='white' size='large' tooltip={lang.massImport2.buttons.reset.tooltip} />
      <CancelButton className={classes.dataImportCancelButton} label={lang.cancelButtonLabel} onClick={abort} />
      <ButtonIconSquare
        key='data-import-component-d'
        label={isImporting ? texts.importRunning : texts.button.import}
        disabled={isImporting}
        icon={IconUploadFile}
        className={classes.dataImportContinueButton}
        onClick={errors.length ? showErrors : onContinue}
        isLoading={isImporting}
        dataQsId='mass-import-button-import' />
    </Topbar>
  )
}

const Master = ({ classes, fileName, uploader, boolProps, importLineProps, importOptionProps, importType }) => (
  <div className={classes.master}>
    <span className={classes.label}>{texts.fileLabel}</span>
    <FileInput multiple accept={acceptAttribute} uploader={uploader} >
      <TextInputWithIcon useFormik={false} icon={IconFolder} value={fileName} name='fileName' />
    </FileInput>
    {importType === IMPORT_TYPE_CUSTOMERS && (
      <div className={classes.faqLinkContainer}>
        <a
          href={CUSTOMER_IMPORT_FAQ_URL}
          target="_blank"
          rel="noopener noreferrer"
          className={classes.faqLink}
        >
          {texts.faqLink}
        </a>
      </div>
    )}
    <span className={classes.label}>{texts.importKind}</span>
    <RadioInput
      customOptionClass={classes.customOptionClass}
      customClass={classes.radio}
      customOptionContainerClass={classes.radioOptionsContainer}
      name='changeShopSyncOptions'
      {...importOptionProps}
    />
    <DOAccordion title={texts.options} expanded={true} content={
      <div className={classes.moreOptions}>
        <OptionImportLine {...importLineProps} />
        <OptionBool {...boolProps} />
      </div>
    } />
  </div>
)

const Detail = (props) => {
  const { classes, onChangeAssignmentData } = props
  const perfectScrollbarRef = useRef(null)

  return (
    <PerfectScrollbar
      component='div'
      ref={perfectScrollbarRef}
      className={classes.scrollbarContainer}
      options={{ suppressScrollX: true }} >
      <div className={classes.detail}>
        <MassImportPreview {...props} />
        <MassImportAssignment {...props} onChange={onChangeAssignmentData} />
      </div>
    </PerfectScrollbar>
  )
}

const transformWorkBook = (binaryStr) => {
  const workBook = read(binaryStr, { type: 'binary' })
  const firstSheetName = workBook.SheetNames[0]
  const worksheet = workBook.Sheets[firstSheetName]

  // find header columns
  const cells = Object.keys(worksheet)
  const rowIndexMatch = getRowIndex(cells)
  const data = cells.reduce((result, current) => {
    const expr = /([A-Z]+)(\d+)/
    const match = expr.exec(current)
    if (match && match.length > 0) {
      const rowIndex = match[2]
      if (rowIndex === rowIndexMatch.toString()) {
        result.columns[match[1]] = worksheet[match[0]].w
      } else {
        // result.rows.push(worksheet[match[0]].v ? worksheet[match[0]].v : '')
        if (!result.rows[rowIndex]) {
          result.rows[rowIndex] = []
        }
        result.rows[rowIndex] = {
          ...result.rows[rowIndex] || {},
          [match[1]]: {
            value: (worksheet[match[0]].v || isNumber(worksheet[match[0]].v)) ? worksheet[match[0]].v : '',
            excelId: match[0],
          },
        }
      }
    }
    return result
  }, { columns: {}, rows: [] })

  const { columns, rows } = data

  return {
    columns,
    rows,
  }
}
